import { z } from 'zod';
import { EFFECT_IDS } from '@/lib/editor/effects';
import { MUSIC_LIBRARY } from '@/lib/editor/musicLibrary';
import { OVERLAY_TEMPLATES } from '@/lib/editor/overlayTemplates';
import { STICKER_PACKS } from '@/lib/editor/stickerPacks';
import { TRANSITION_TYPES } from '@/lib/editor/transitions';

const effectIds = EFFECT_IDS as unknown as [string, ...string[]];
const musicIds = MUSIC_LIBRARY.map((track) => track.id) as [string, ...string[]];
const templateIds = OVERLAY_TEMPLATES.map((template) => template.id) as [string, ...string[]];
const stickerIds = STICKER_PACKS.flatMap((pack) => pack.stickers.map((sticker) => sticker.id)) as [string, ...string[]];
const transitionTypes = TRANSITION_TYPES as [string, ...string[]];

/**
 * Models and humans commonly express opacity/gain as either a normalized
 * ratio (`0.99`) or a percentage (`99`).  Accept both at the tool boundary
 * and normalize before the value reaches the editor store.  Keeping this in
 * the schema also makes retries deterministic instead of relying on the
 * model to repair an otherwise valid user instruction.
 */
const normalizedPercent = (label: string) =>
  z
    .number()
    .min(0)
    .max(100)
    .transform((value) => (value > 1 ? value / 100 : value))
    .describe(`${label}: можно передать как 0..1 или как проценты 0..100 (99% = 99)`);

// Модель-агностичные определения инструментов МОНТАЖА. Без серверного execute —
// это client-side tools (вариант A): AI SDK стримит вызов в браузер, где хук
// применяет его к Zustand-стору для живого превью.
export const EDITING_TOOLS = {
  addClip: {
    description: 'Добавить в конец таймлайна клип из библиотеки пользователя по его mediaId. Используй только id из предоставленного списка доступных медиа.',
    inputSchema: z.object({ mediaId: z.string().describe('EditorMedia.id из списка библиотеки') }),
  },
  addClipSegment: {
    description: 'Добавить найденный по содержимому фрагмент клипа с точными таймкодами. Используй mediaId/startSec/endSec только из результата searchLibraryContent.',
    inputSchema: z.object({
      mediaId: z.string(),
      startSec: z.number().min(0),
      endSec: z.number().positive(),
    }).refine((value) => value.endSec > value.startSec, { message: 'endSec должен быть больше startSec' }),
  },
  removeClip: {
    description: 'Удалить клип с таймлайна по clipId.',
    inputSchema: z.object({ clipId: z.string() }),
  },
  duplicateClip: {
    description: 'Продублировать клип на таймлайне по clipId.',
    inputSchema: z.object({ clipId: z.string() }),
  },
  trimClip: {
    description: 'Подрезать клип: сдвинуть его начало (in) или конец (out) на deltaSeconds (может быть отрицательным). Минимальная длительность клипа 0.5с.',
    inputSchema: z.object({
      clipId: z.string(),
      edge: z.enum(['in', 'out']),
      deltaSeconds: z.number(),
    }),
  },
  splitClip: {
    description: 'Разрезать активный клип в текущей позиции плейхеда на два. Перед вызовом при необходимости сдвинь плейхед не требуется — используется текущая позиция.',
    inputSchema: z.object({}),
  },
  reorderClip: {
    description: 'Переместить клип по clipId на новую позицию toIndex (0 — первый) в порядке таймлайна.',
    inputSchema: z.object({ clipId: z.string(), toIndex: z.number().int().min(0) }),
  },
  updateClipProperties: {
    description:
      'Изменить монтажные свойства конкретного клипа: скорость, громкость, кадрирование, положение, масштаб, поворот, прозрачность или фильтр. ' +
      'Передавай только те поля, которые нужно изменить. Громкость и прозрачность принимают и долю 0..1, и человеческие проценты 0..100.',
    inputSchema: z.object({
      clipId: z.string(),
      playbackRate: z.number().min(0.25).max(4).optional(),
      volume: normalizedPercent('Громкость клипа').optional(),
      fit: z.enum(['contain', 'cover']).optional(),
      positionX: z.number().min(0).max(100).optional(),
      positionY: z.number().min(0).max(100).optional(),
      scale: z.number().min(0.1).max(4).optional(),
      rotation: z.number().min(-360).max(360).optional(),
      opacity: normalizedPercent('Прозрачность клипа').optional(),
      filterId: z.string().optional().describe('ID фильтра из контекста или none'),
    }).refine((value) => Object.keys(value).some((key) => key !== 'clipId'), {
      message: 'Нужно передать хотя бы одно изменяемое свойство',
    }),
  },
  setTrackState: {
    description: 'Заблокировать, скрыть или заглушить дорожку video, music или overlays. Передавай только нужные состояния.',
    inputSchema: z.object({
      track: z.enum(['video', 'music', 'overlays']),
      locked: z.boolean().optional(),
      hidden: z.boolean().optional(),
      muted: z.boolean().optional(),
    }).refine((value) => value.locked !== undefined || value.hidden !== undefined || value.muted !== undefined, {
      message: 'Нужно передать хотя бы одно состояние',
    }),
  },
  setClipTransition: {
    description:
      'Поставить переход между двумя соседними видеоклипами. clipId — ID ВТОРОГО клипа в стыке; переход появится на его входе, то есть в конце предыдущего клипа.',
    inputSchema: z.object({
      clipId: z.string(),
      type: z.enum(transitionTypes),
      durationSec: z.number().min(0.2).max(2).default(0.5),
    }),
  },
  removeClipTransition: {
    description: 'Удалить входящий переход у клипа. clipId — ID второго клипа в стыке.',
    inputSchema: z.object({ clipId: z.string() }),
  },
  addText: {
    description: 'Добавить текстовый слой-оверлей. Координаты x,y в процентах 0..100 (центр = 50,50). size — кегль в px.',
    inputSchema: z.object({
      text: z.string(),
      x: z.number().min(0).max(100).optional(),
      y: z.number().min(0).max(100).optional(),
      size: z.number().min(8).max(200).optional(),
      color: z.string().optional().describe('CSS-цвет текста, напр. #FFFFFF'),
    }),
  },
  addCaption: {
    description: 'Добавить редактируемый субтитр в нижней части кадра на заданном отрезке таймлайна.',
    inputSchema: z.object({
      text: z.string().min(1),
      start: z.number().min(0).optional(),
      end: z.number().min(0).optional(),
      x: z.number().min(0).max(100).optional(),
      y: z.number().min(0).max(100).optional(),
      size: z.number().min(16).max(240).optional(),
      color: z.string().optional(),
    }).refine((value) => value.end === undefined || value.start === undefined || value.end > value.start, {
      message: 'end должен быть больше start',
    }),
  },
  addSticker: {
    description: 'Добавить готовый стикер из каталога редактора. Используй только доступный stickerId.',
    inputSchema: z.object({
      stickerId: z.enum(stickerIds),
      x: z.number().min(0).max(100).optional(),
      y: z.number().min(0).max(100).optional(),
      widthPercent: z.number().min(5).max(100).optional(),
      start: z.number().min(0).optional(),
      end: z.number().min(0).optional(),
    }).refine((value) => value.end === undefined || value.start === undefined || value.end > value.start, {
      message: 'end должен быть больше start',
    }),
  },
  applyTemplate: {
    description: 'Применить готовый многослойный шаблон редактора в позиции плейхеда. Все элементы останутся редактируемыми.',
    inputSchema: z.object({ templateId: z.enum(templateIds) }),
  },
  applyEffect: {
    description: 'Применить визуальный эффект-оверлей на всю сцену. effectSrc — идентификатор/URL эффекта, label — человекочитаемое имя.',
    inputSchema: z.object({ effectSrc: z.string(), label: z.string() }),
  },
  addCatalogEffect: {
    description: 'Добавить визуальный эффект из встроенного каталога на текущую позицию таймлайна.',
    inputSchema: z.object({ effectId: z.enum(effectIds) }),
  },
  addMusic: {
    description: 'Добавить фоновую музыку из встроенной библиотеки. Используй только доступный trackId.',
    inputSchema: z.object({
      trackId: z.enum(musicIds),
      start: z.number().min(0).optional(),
      volume: normalizedPercent('Громкость музыки').optional(),
      fadeIn: z.number().min(0).max(10).optional(),
      fadeOut: z.number().min(0).max(10).optional(),
      ducking: z.boolean().optional(),
    }),
  },
  updateAudioTrack: {
    description: 'Изменить конкретную музыкальную дорожку: громкость, фейды, ducking, mute/solo, панораму или положение.',
    inputSchema: z.object({
      trackId: z.string(),
      volume: normalizedPercent('Громкость музыки').optional(),
      fadeIn: z.number().min(0).max(10).optional(),
      fadeOut: z.number().min(0).max(10).optional(),
      ducking: z.boolean().optional(),
      muted: z.boolean().optional(),
      solo: z.boolean().optional(),
      pan: z.number().min(-1).max(1).optional(),
      start: z.number().min(0).optional(),
      timelineDuration: z.number().min(0.1).optional(),
      sourceOffset: z.number().min(0).optional(),
    }).refine((value) => Object.keys(value).some((key) => key !== 'trackId'), {
      message: 'Нужно передать хотя бы одно изменяемое свойство',
    }),
  },
  removeAudioTrack: {
    description: 'Удалить музыкальную дорожку по её trackId.',
    inputSchema: z.object({ trackId: z.string() }),
  },
  setAspectRatio: {
    description: 'Сменить формат кадра проекта.',
    inputSchema: z.object({ aspectRatio: z.enum(['9:16', '16:9', '1:1']) }),
  },
  setCrossfade: {
    description: 'Задать длительность кроссфейда-перехода между клипами, в секундах (0..2).',
    inputSchema: z.object({ seconds: z.number().min(0).max(2) }),
  },
  updateLayer: {
    description: 'Изменить свойства существующего слоя-оверлея по layerId (текст, позиция x/y в %, size, color, времена start/end в секундах, opacity как 0..1 или 0..100%).',
    inputSchema: z.object({
      layerId: z.string(),
      text: z.string().optional(),
      x: z.number().min(0).max(100).optional(),
      y: z.number().min(0).max(100).optional(),
      size: z.number().min(8).max(200).optional(),
      color: z.string().optional(),
      start: z.number().min(0).optional(),
      end: z.number().min(0).optional(),
      opacity: normalizedPercent('Прозрачность слоя').optional(),
    }),
  },
  setLayerTransition: {
    description: 'Добавить плавное появление или исчезновение для текста, стикера, эффекта или другого слоя.',
    inputSchema: z.object({
      layerId: z.string(),
      edge: z.enum(['in', 'out']),
      type: z.enum(transitionTypes),
      durationSec: z.number().min(0.2).max(2).default(0.5),
    }),
  },
  addLayerKeyframe: {
    description: 'Поставить ключевой кадр слоя на заданном времени. Можно одновременно задать позицию, размер, поворот и прозрачность.',
    inputSchema: z.object({
      layerId: z.string(),
      time: z.number().min(0),
      x: z.number().min(0).max(100).optional(),
      y: z.number().min(0).max(100).optional(),
      size: z.number().min(8).max(500).optional(),
      widthPercent: z.number().min(2).max(100).optional(),
      rotation: z.number().min(-360).max(360).optional(),
      opacity: normalizedPercent('Прозрачность ключевого кадра').optional(),
    }),
  },
  removeLayer: {
    description: 'Удалить слой-оверлей по layerId.',
    inputSchema: z.object({ layerId: z.string() }),
  },
  // Схема соответствует GenShot из src/lib/editor/agent/generation.ts (тип
  // намеренно продублирован здесь как Zod, не импортирован — tools.ts
  // используется и на клиенте, и в API route, а generation.ts тянет
  // wavespeed-специфичные константы/комментарии, которые тут не нужны).
  // Как и остальные EDITING_TOOLS — client-side tool, без execute: сервер
  // лишь стримит вызов, подтверждение и запуск генерации отрабатывает клиент.
  proposeGeneration: {
    description:
      'Предложить пользователю сториборд для ГЕНЕРАЦИИ новых видео/картинок (когда нужных сцен нет в библиотеке). ' +
      'Показывается как карточка с подтверждением — реальная генерация запускается только после явного согласия ' +
      'пользователя (это платно). Не подрезает и не монтирует существующие клипы — только предлагает план съёмки.',
    inputSchema: z.object({
      summary: z.string().min(1).describe('Короткое резюме всей истории/ролика на русском'),
      shots: z
        .array(
          z.object({
            order: z.number().int().min(0).describe('Порядковый номер шота, начиная с 0'),
            description: z.string().min(1).describe('Что происходит в шоте, человекочитаемо'),
            kind: z.enum(['video', 'image']),
            prompt: z.string().min(1).describe('Короткий, яркий промпт для генеративной модели (на английском предпочтительно)'),
            useReferenceImage: z.boolean().optional().describe('true — использовать приложенный референс для консистентности персонажа'),
            durationSec: z.number().optional().describe('Длительность видео-шота в секундах (только для kind=video)'),
            aspectRatio: z.enum(['9:16', '16:9', '1:1']).optional(),
          })
        )
        .min(1)
        .describe('Список шотов сториборда, минимум 1'),
    }),
  },
} as const;

export type EditingToolName = keyof typeof EDITING_TOOLS;
